import React from 'react';
import { ButtonBase } from '@mui/material';

export interface NameLinkProps {
  /** The row's name, as it would read unlinked. */
  children: React.ReactNode;
  onClick: () => void;
  /** What a click opens, for screen readers: "Calculator, by hour of day". */
  'aria-label'?: string;
}

/**
 * A row's name that opens that row's detail in a window — a campaign on a
 * grid opening its hours. Reads as the name it is, in the row's own type and
 * ink, and wraps like one: a long name is never cut. Hover and focus mark it
 * as something to click; a dotted underline says so at rest without shouting
 * across a column of names.
 *
 * For a detail opened under the row instead, use `DisclosureLink`.
 */
export function NameLink({ children, onClick, ...aria }: NameLinkProps) {
  return (
    <ButtonBase
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-haspopup="dialog"
      {...aria}
      sx={(t) => ({
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left',
        justifyContent: 'flex-start',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        borderRadius: `${t.ds.radius.xs}px`,
        textDecoration: 'underline dotted',
        textDecorationColor: t.tokens.border.strong,
        textUnderlineOffset: '3px',
        transition: `color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
        '&:hover': { color: t.tokens.brand.primary, textDecorationColor: t.tokens.brand.primary },
        '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
      })}
    >
      {children}
    </ButtonBase>
  );
}
